import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@proctoring/api-client';
import type {
  CancelInterviewRequest,
  CreateInterviewRequest,
  CreateInterviewerRequest,
  InterviewState,
  JobProfileInput,
  JobProfileUpdate,
  RescheduleInterviewRequest,
} from '@proctoring/shared';
import { useApi } from '../auth/AuthProvider.js';

export const queryKeys = {
  jobProfiles: ['admin', 'job-profiles'] as const,
  interviewers: ['admin', 'interviewers'] as const,
  interviews: (state?: InterviewState) => ['admin', 'interviews', state ?? 'all'] as const,
  interview: (id: string) => ['admin', 'interview', id] as const,
  invitations: (id: string) => ['admin', 'invitations', id] as const,
  telemetry: (id: string) => ['admin', 'telemetry', id] as const,
};

export function useJobProfiles() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.jobProfiles,
    queryFn: ({ signal }) => api.jobProfiles(signal),
  });
}

export function useInterviewers() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interviewers,
    queryFn: ({ signal }) => api.adminInterviewers(signal),
  });
}

export function useInterviews(state?: InterviewState) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interviews(state),
    queryFn: ({ signal }) => api.adminInterviews(state ? { state } : {}, signal),
  });
}

export function useInterview(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interview(id),
    queryFn: ({ signal }) => api.adminInterview(id, signal),
    enabled: id.length > 0,
  });
}

export function useInvitations(interviewId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.invitations(interviewId),
    queryFn: ({ signal }) => api.interviewInvitations(interviewId, signal),
    enabled: interviewId.length > 0,
  });
}

export function useTelemetry(interviewId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.telemetry(interviewId),
    queryFn: ({ signal }) => api.adminTelemetry(interviewId, signal),
    enabled: interviewId.length > 0,
  });
}

// --- Mutations ----------------------------------------------------------------

export function useCreateJobProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: JobProfileInput) => api.createJobProfile(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.jobProfiles }),
  });
}

export function useUpdateJobProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: JobProfileUpdate }) =>
      api.updateJobProfile(id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.jobProfiles }),
  });
}

export function useDeleteJobProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteJobProfile(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.jobProfiles }),
  });
}

/**
 * Invites an interviewer.
 *
 * Invalidating the interviewers query refreshes both this page's list and the interviewer
 * picker on the new-interview form, so a freshly invited account is selectable immediately.
 */
export function useCreateInterviewer() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInterviewerRequest) => api.createInterviewer(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.interviewers }),
  });
}

export function useCreateInterview() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInterviewRequest) => api.createInterview(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'interviews'] }),
  });
}

/**
 * Cancels an interview.
 *
 * Invalidates the invitation list alongside the interview itself: cancelling revokes every
 * outstanding setup link server-side, so a detail page that refreshed only the header would
 * keep showing "Pending" badges for tokens that are already dead.
 */
export function useCancelInterview(interviewId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelInterviewRequest) => api.cancelInterview(interviewId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.interview(interviewId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations(interviewId) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'interviews'] });
    },
  });
}

export function useRescheduleInterview(interviewId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RescheduleInterviewRequest) => api.rescheduleInterview(interviewId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.interview(interviewId) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'interviews'] });
    },
  });
}

export function useResendInvitation(interviewId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.resendInvitation(invitationId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations(interviewId) }),
  });
}

export function useRevokeInvitation(interviewId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.revokeInvitation(invitationId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations(interviewId) }),
  });
}

/**
 * Downloads a telemetry export.
 *
 * Done through the client rather than an `<a href>` because the endpoint is bearer
 * authenticated — a plain link would arrive without the JWT and 401.
 */
export async function downloadTelemetryExport(
  api: ApiClient,
  interviewId: string,
  format: 'json' | 'csv',
): Promise<void> {
  const blob = await api.adminTelemetryExport(interviewId, format);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `telemetry-${interviewId}.${format}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
